import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import {MatDialogActions, MatDialogContent, MatDialogRef} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatInputModule} from '@angular/material/input';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  clearUserLLMModelSetting,
  defaultUserLLMModelSetting,
  LlmType,
  loadUserLLMModelSetting,
  MaxTokenPerModel,
  saveUserLLMModelSetting,
  UserLLMModelSetting
} from '../../models/llm-setting';
import {MatRadioModule} from '@angular/material/radio';
import {DeviceIdService} from '../../services/device-id.service';
import {MatIcon} from '@angular/material/icon';
import {deleteEmbeddingsForUser} from '../../../modules/chat/service/embedding.actions';
import {Store} from '@ngrx/store';
import {environment} from '../../../../environments/environment';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatDivider} from '@angular/material/divider';
import {CloseableDialogComponent} from '../closeable-dialog/closeable-dialog.component';


interface OllamaModel {
  value: string;
  viewValue: string;
}


@Component({
  selector: 'app-llm-model-setting-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
    MatRadioModule,
    ReactiveFormsModule,
    MatIcon,
    MatExpansionModule,
    MatDivider,
    CloseableDialogComponent
  ],
  templateUrl: './llm-model-setting-dialog.component.html',
  styleUrl: './llm-model-setting-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LlmModelSettingDialogComponent implements OnInit {
  private deviceIdService: DeviceIdService = inject(DeviceIdService);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly store: Store = inject(Store);
  private readonly dialogRef: MatDialogRef<LlmModelSettingDialogComponent> = inject(MatDialogRef);

  deviceId?: string;

  currentMaxTokens: number = 32768

  //https://ollama.com/search?c=tools
  ollamaModels: OllamaModel[] = [
    {value: 'mistral:7b', viewValue: 'mistral:7b'},
    {value: 'gemma2:9b', viewValue: 'gemma2:9b'},
    {value: 'llama3.2:latest', viewValue: 'llama3.2:latest'},
    {value: 'llama3.3:70b', viewValue: 'llama3.3:70b'},
    {value: 'gemma2:27b', viewValue: 'gemma2:27b'},
    {value: 'gemma3:latest', viewValue: 'gemma3:latest'},
    {value: 'smollm2:latest', viewValue: 'smollm2:latest'},
    {value: 'qwen2.5:latest', viewValue: 'qwen2.5:latest'},
    {value: 'deepseek-r1:8b', viewValue: 'deepseek-r1:8b'},
    {value: 'gpt-oss:latest', viewValue: 'gpt-oss:latest'},
    {value: 'gpt-oss:20b', viewValue: 'gpt-oss:20b'},
    {value: 'qwen3:8b', viewValue: 'qwen3:8b'},
  ];

  ollamaEmbeddingModels: OllamaModel[] = [
    {value: 'nomic-embed-text:latest', viewValue: 'nomic-embed-text'}, //768 vector size
    {value: 'paraphrase-multilingual:latest', viewValue: 'paraphrase-multilingual'}, //768 vector size
    //{value: 'mxbai-embed-large:335m', viewValue: 'mxbai-embed-large'}, //1024 vector size
    //{value: 'all-minilm', viewValue: 'all-minilm'}, //384 vector size
    //{value: 'snowflake-arctic-embed:335m', viewValue: 'snowflake-arctic-embed'},  //1024 vector size
    //{value: 'bge-m3:567m', viewValue: 'bge-m3'}, //1024 vector size
    //{value: 'snowflake-arctic-embed2:latest', viewValue: 'snowflake-arctic-embed2'}, //1024 vector size
    //{value: 'granite-embedding:latest', viewValue: 'granite-embedding'}, //1024 vector size
  ];

  //https://platform.openai.com/docs/models
  chatGPTModels: OllamaModel[] = [
    {value: 'gpt-5.1-2025-11-13', viewValue: 'gpt-5.1'},
    {value: 'gpt-5-mini-2025-08-07', viewValue: 'gpt-5-mini'},
    {value: 'gpt-5-pro-2025-10-06', viewValue: 'gpt-5-pro'},
    {value: 'gpt-5-nano-2025-08-07', viewValue: 'gpt-5-nano'},
  ];

  //https://platform.openai.com/docs/guides/embeddings/embedding-models
  chatGPTEmbeddingModels: OllamaModel[] = [
    {value: 'text-embedding-3-small', viewValue: 'text-embedding-3-small'},
    {value: 'text-embedding-3-large', viewValue: 'text-embedding-3-large'},
    // {value: 'text-embedding-ada-002', viewValue: 'text-embedding-ada-002'}, doesn't support size 1024 vector size
  ];

  //https://platform.openai.com/docs/models
  geminiGPTModels: OllamaModel[] = [
    {value: 'gemini-2.0-flash', viewValue: 'gemini-2.0-flash'},
    {value: 'gemini-3-pro-preview', viewValue: 'gemini-3-pro preview'},
    {value: 'gemini-2.5-pro', viewValue: 'gemini-2.5-pro'},
  ];

  //https://platform.openai.com/docs/guides/embeddings/embedding-models
  geminiGPTEmbeddingModels: OllamaModel[] = [
    {value: 'text-embedding-004', viewValue: 'text-embedding-004'},
  ];


  form: FormGroup = this.fb.group(
    {
      selectedLLM: ['none', Validators.required],
      ollamaBaseUrl: [''],
      ollamaModel: [''],
      ollamaEmbeddingModel: [''],

      chatGptModel: [''],
      chatGptApiKey: [''],
      chatGptEmbeddingModel: [''],

      geminiModel: [''],
      geminiApiKey: [''],
      geminiEmbeddingModel: [''],

      // Advanced settings
      temperature: [0.0, [Validators.required, Validators.min(0), Validators.max(2)]],
      topK: [100, [Validators.required, Validators.min(0)]],
      topP: [0.95, [Validators.required, Validators.min(0), Validators.max(1)]],
      repeatPenalty: [1.1, [Validators.required, Validators.min(1)]],
      seed: [28, Validators.required],
      numPredict: [-1, [Validators.required, Validators.min(-1), Validators.max(8192)]],
      numCtx: [25000, [Validators.required, Validators.min(1), Validators.max(32768)]],

      //QUERY KEYS
      pubMedApiKey: [''],
      scopusApiKey: [''],
      semanticScholarApiKey: [''],
    }
  );

  setting!: UserLLMModelSetting;

  ngOnInit(): void {
    this.deviceId = this.deviceIdService.getDeviceId();
    this.setting = loadUserLLMModelSetting(true)!;

    this.form.get('selectedLLM')?.valueChanges.subscribe(selected => {
      const ollamaValidators = selected === LlmType.OLLAMA ? [Validators.required] : [];
      this.form.get('ollamaModel')?.setValidators(ollamaValidators);
      this.form.get('ollamaEmbeddingModel')?.setValidators(ollamaValidators);

      const openAiValidators = selected === LlmType.CHATGPT ? [Validators.required] : [];
      this.form.get('chatGptModel')?.setValidators(openAiValidators);
      this.form.get('chatGptApiKey')?.setValidators(openAiValidators);
      this.form.get('chatGptEmbeddingModel')?.setValidators(openAiValidators);

      const geminiValidators = selected === LlmType.GEMINI ? [Validators.required] : [];
      this.form.get('geminiModel')?.setValidators(geminiValidators);
      this.form.get('geminiApiKey')?.setValidators(geminiValidators);
      this.form.get('geminiEmbeddingModel')?.setValidators(geminiValidators);
      this.form.updateValueAndValidity();
    });

    this.form.get('ollamaModel')?.valueChanges.subscribe(model => {
      if (model && this.isOllamaEnabled()) {
        this.updateMaxTokens(model);
      }
    });

    this.form.get('chatGptModel')?.valueChanges.subscribe(model => {
      if (model && this.isChatGptEnabled()) {
        this.updateMaxTokens(model);
      }
    });

    this.form.get('geminiModel')?.valueChanges.subscribe(model => {
      if (model && this.isGeminiEnabled()) {
        this.updateMaxTokens(model);
      }
    });

    this.form.patchValue(this.setting);

    this.cdr.detectChanges();
  }

  updateMaxTokens(modelValue: string): void {
    const maxTokens = MaxTokenPerModel.getMaxTokens(modelValue);

    this.form.get('numCtx')?.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(maxTokens)
    ]);

    const currentValue = this.form.get('numCtx')?.value;
    if (currentValue > maxTokens) {
      this.form.get('numCtx')?.setValue(maxTokens);
    }

    this.currentMaxTokens = maxTokens;

    this.form.get('numCtx')?.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  clearSetting(): void {
    this.setting = defaultUserLLMModelSetting;
    this.form.patchValue(this.setting);
    this.cdr.detectChanges();
  }

  deleteSettings(): void {
    clearUserLLMModelSetting();
    this.clearSetting();
  }

  onConfirm(): void {
    const needsClear = this.needsEmbeddingClear();
    this.setting = this.form.value;
    saveUserLLMModelSetting(this.setting);
    if (needsClear) {
      this.store.dispatch(deleteEmbeddingsForUser());
    }
    this.dialogRef.close(true);
  }

  setDeviceIdToTest() {
    if (!this.isLocalDevelopment) {
      return;
    }
    this.deviceIdService.setToTest();
    this.deviceId = this.deviceIdService.getDeviceId();
  }

  needsEmbeddingClear(): boolean {
    if (this.setting.selectedLLM !== this.form.get('selectedLLM')?.value) {
      return true;
    }
    if (this.isOllamaEnabled() && this.form.get('ollamaEmbeddingModel')?.value !== this.setting.ollamaEmbeddingModel) {
      return true;
    }
    if (this.isChatGptEnabled() && this.form.get('chatGptEmbeddingModel')?.value !== this.setting.chatGptEmbeddingModel) {
      return true;
    }
    return false;
  }

  isOllamaEnabled(): boolean {
    return this.form.get('selectedLLM')?.value === LlmType.OLLAMA;
  }

  isChatGptEnabled(): boolean {
    return this.form.get('selectedLLM')?.value === LlmType.CHATGPT;
  }

  isGeminiEnabled(): boolean {
    return this.form.get('selectedLLM')?.value === LlmType.GEMINI;
  }

  toggleMaximize(isMaximized: boolean): void {
    if (isMaximized) {
      this.dialogRef.updateSize('100vw', '100vh');
    } else {
      this.dialogRef.updateSize('600px', '600px');
    }
  }

  protected readonly LlmType = LlmType;
  protected readonly isProduction: boolean = environment.production;
  // Only for local development (ng serve); staging and production builds never show the test-user switch
  protected readonly isLocalDevelopment: boolean = environment.stage === 'local';
}
